import { useEffect } from 'react';

/**
 * スクロールで要素を出現させる。
 *
 * 対象は `data-rv="up"`（下から浮かび上がる）と `data-rv="curtain"`（覆いが外れる）。
 *
 * 重要な設計判断が3つある。いずれも「コンテンツが永久に見えない」事故を防ぐためのもの。
 *
 * 1. 要素そのものを clip-path で潰して隠さない。高さを0にすると
 *    IntersectionObserver が交差を検出できず、表示用クラスが永久に付かない。
 *    覆いは疑似要素で被せ、要素自体は常に通常のサイズで存在させる。
 * 2. 動きを有効にするクラスはJSが動いたときだけ付ける。HTMLに直書きすると
 *    JSが無効な環境で全要素が非表示のままになる。
 * 3. 読み込み時に画面内にあるものは即表示し、一定時間経っても未表示のものは
 *    強制的に表示する。何が起きても見えないままにはしない。
 *
 * @param {React.RefObject<HTMLElement>} rootRef 対象を探す範囲
 * @param {boolean} enabled 動きを有効にするか
 */
export default function useScrollReveal(rootRef, enabled = true) {
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;

    const targets = Array.from(root.querySelectorAll('[data-rv]'));
    if (targets.length === 0) return undefined;

    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (!enabled || reduce) {
      targets.forEach((el) => el.classList.add('is-in'));
      return undefined;
    }

    // JSが動いた証としてここで初めて付ける
    root.classList.add('rv-on');

    const reveal = (el) => {
      if (el.classList.contains('is-in')) return;
      const siblings = Array.from(el.parentElement ? el.parentElement.children : [])
        .filter((n) => n.hasAttribute('data-rv'));
      const order = Math.max(0, siblings.indexOf(el));
      el.style.transitionDelay = `${Math.min(order, 6) * 60}ms`;
      el.classList.add('is-in');
    };

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (!e.isIntersecting) return;
          reveal(e.target);
          io.unobserve(e.target);
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -6% 0px' }
    );
    targets.forEach((el) => io.observe(el));

    // 安全網1: 初期表示の時点で画面内にあるものは待たずに出す
    const raf = requestAnimationFrame(() => {
      targets.forEach((el) => {
        const r = el.getBoundingClientRect();
        if (r.top < window.innerHeight && r.bottom > 0) {
          reveal(el);
          io.unobserve(el);
        }
      });
    });

    // 安全網2: 4秒経っても出ていないものは強制的に出す
    const timer = setTimeout(() => targets.forEach(reveal), 4000);

    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(timer);
      io.disconnect();
      root.classList.remove('rv-on');
    };
  }, [rootRef, enabled]);
}
